import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { NetWorthSeriesRow } from "../../lib/api-modules/types.js";

/**
 * Portfolio + per-account performance chart. Phase 16e (2026-05-10).
 *
 * Different shape from `<NetWorthChart>` — multi-line, every series
 * normalized to 100 at chart-start (or the series' first non-zero
 * date if it opened later in the window), no gradient-filled area.
 * Answers "is my Roth tracking faster than my IRRA, and either of
 * them faster than SPY?" — where the absolute-dollar levels would
 * dominate the visual.
 *
 * Series:
 *   - **Household** primary line — `portfolio_value_cents` per
 *     snapshot, normalized to 100 at the first row's value.
 *     Thicker stroke so it visually anchors the chart.
 *   - **Per-account** lines — `portfolio_by_account[account_id]` per
 *     snapshot, normalized to 100 at the first non-zero value (so
 *     accounts that opened mid-window start at 100 on their first
 *     positions-bearing day).
 *   - **Index overlays** (SPY/QQQ/VT/+Custom) — close prices
 *     normalized to 100 on the first close on-or-after the first
 *     snapshot date.
 *
 * Click a per-account metric card → set `highlightedAccountId` to
 * thicken that line + dim the others.
 */

export interface PerformanceChartAccount {
  readonly account_id: string;
  readonly name: string;
  readonly color: string;
}

export interface PerformanceChartOverlay {
  readonly key: string;
  readonly label: string;
  readonly color: string;
  readonly bars: ReadonlyArray<{ readonly date: string; readonly close_cents: number }>;
}

interface PortfolioPerformanceChartProps {
  readonly snapshots: readonly NetWorthSeriesRow[];
  readonly accounts: readonly PerformanceChartAccount[];
  readonly overlays?: readonly PerformanceChartOverlay[];
  /** account_id of the metric card the user is hovering / clicked. */
  readonly highlightedAccountId?: string | null;
  readonly hideValues: boolean;
}

interface ChartPoint {
  date: string;
  /** Normalized household line — 100-anchored. */
  household?: number;
  [seriesKey: string]: number | string | undefined;
}

const HOUSEHOLD_COLOR = "var(--color-accent)";
const HOUSEHOLD_KEY = "household";

export function PortfolioPerformanceChart({
  snapshots,
  accounts,
  overlays,
  highlightedAccountId,
  hideValues,
}: PortfolioPerformanceChartProps) {
  const data = useMemo<ChartPoint[]>(() => {
    if (snapshots.length === 0) return [];

    // Household basis = first snapshot's portfolio_value_cents.
    const firstHousehold = snapshots.find(
      (s) => (s.portfolio_value_cents ?? 0) > 0,
    );
    const householdBasis = firstHousehold?.portfolio_value_cents ?? 0;

    // Per-account basis = each account's first non-zero value in the
    // window. Accounts that opened mid-window start their normalized
    // line at 100 on their first positions-bearing day; earlier dates
    // render no point and recharts leaves a gap.
    const accountBasis = new Map<string, number>();
    for (const acct of accounts) {
      for (const s of snapshots) {
        const v = s.portfolio_by_account?.[acct.account_id];
        if (typeof v === "number" && v > 0) {
          accountBasis.set(acct.account_id, v);
          break;
        }
      }
    }

    // Overlay basis = first close on-or-after the first snapshot date.
    const overlayBarsByKey = new Map<string, Map<string, number>>();
    const overlayBasisByKey = new Map<string, number>();
    const firstSnapDate = snapshots[0]?.date;
    for (const ov of overlays ?? []) {
      const byDate = new Map<string, number>();
      for (const bar of ov.bars) byDate.set(bar.date, bar.close_cents);
      overlayBarsByKey.set(ov.key, byDate);
      let basis: number | undefined;
      if (firstSnapDate) {
        for (const bar of ov.bars) {
          if (bar.date >= firstSnapDate) {
            basis = bar.close_cents;
            break;
          }
        }
      }
      basis ??= ov.bars[0]?.close_cents;
      if (basis !== undefined && basis > 0) overlayBasisByKey.set(ov.key, basis);
    }

    return snapshots.map((s) => {
      const point: ChartPoint = { date: s.date };
      if (householdBasis > 0 && typeof s.portfolio_value_cents === "number") {
        point[HOUSEHOLD_KEY] = (s.portfolio_value_cents / householdBasis) * 100;
      }
      for (const acct of accounts) {
        const basis = accountBasis.get(acct.account_id);
        const v = s.portfolio_by_account?.[acct.account_id];
        if (basis && typeof v === "number" && v > 0) {
          point[acct.account_id] = (v / basis) * 100;
        }
      }
      for (const ov of overlays ?? []) {
        const basis = overlayBasisByKey.get(ov.key);
        const cv = overlayBarsByKey.get(ov.key)?.get(s.date);
        if (basis && typeof cv === "number") {
          point[ov.key] = (cv / basis) * 100;
        }
      }
      return point;
    });
  }, [snapshots, accounts, overlays]);

  if (snapshots.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-muted)]"
        data-testid="portfolio-performance-chart-empty"
      >
        No snapshots in the selected window — the nightly snapshot job runs at 7pm ET.
      </div>
    );
  }

  return (
    <div
      className="h-[320px] w-full"
      data-testid="portfolio-performance-chart"
    >
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <LineChart data={data} margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeOpacity={0.1} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={shortDate}
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={(v: number) => (hideValues ? "••••" : `${v.toFixed(0)}`)}
            width={40}
            domain={["auto", "auto"]}
          />
          <Tooltip
            content={
              hideValues
                ? () => null
                : ({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    return (
                      <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                        <p className="font-medium text-[var(--color-text)]">{label}</p>
                        <ul className="mt-1 space-y-0.5">
                          {payload.map((entry, idx) => {
                            const value = entry.value;
                            if (typeof value !== "number") return null;
                            const isHousehold = entry.dataKey === HOUSEHOLD_KEY;
                            return (
                              <li
                                key={`${String(entry.dataKey)}-${idx}`}
                                className="flex items-center justify-between gap-4"
                              >
                                <span
                                  className="flex items-center gap-1.5"
                                  style={{ color: entry.color }}
                                >
                                  <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ background: entry.color }}
                                  />
                                  <span className="text-[var(--color-text)]">
                                    {isHousehold ? "Portfolio (household)" : entry.name}
                                  </span>
                                </span>
                                <span
                                  className="font-mono tabular-nums"
                                  style={{ color: entry.color }}
                                >
                                  {value.toFixed(2)}×
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  }
            }
          />

          {/* Household primary line — thicker, always rendered. */}
          <Line
            type="monotone"
            dataKey={HOUSEHOLD_KEY}
            name="Household"
            stroke={HOUSEHOLD_COLOR}
            strokeWidth={
              highlightedAccountId === null || highlightedAccountId === undefined
                ? 2.5
                : 1.5
            }
            strokeOpacity={
              highlightedAccountId && highlightedAccountId !== HOUSEHOLD_KEY
                ? 0.45
                : 1
            }
            dot={false}
            isAnimationActive={false}
            connectNulls
          />

          {accounts.map((acct) => {
            const isFocused =
              !highlightedAccountId || highlightedAccountId === acct.account_id;
            return (
              <Line
                key={acct.account_id}
                type="monotone"
                dataKey={acct.account_id}
                name={acct.name}
                stroke={acct.color}
                strokeWidth={
                  highlightedAccountId === acct.account_id ? 2.5 : 1.5
                }
                strokeOpacity={isFocused ? 1 : 0.3}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            );
          })}

          {(overlays ?? []).map((ov) => (
            <Line
              key={ov.key}
              type="monotone"
              dataKey={ov.key}
              name={ov.label}
              stroke={ov.color}
              strokeWidth={1.25}
              strokeDasharray="4 3"
              strokeOpacity={highlightedAccountId ? 0.55 : 0.85}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function shortDate(iso: string): string {
  const [y, m] = iso.split("-");
  if (!y || !m) return iso;
  const month = MONTHS[Number(m) - 1] ?? m;
  return `${month} '${y.slice(2)}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
